import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ConfigProvider, DatePicker, Popover } from 'antd';
import { DownOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import useAxiosClient from '../axios-client.js';
import { useStateContext } from '../contexts/ContextProvider.jsx';
import { useFabricTypes } from '../hooks/useFabricTypes.jsx';
import {
  FilterChip, LoadingRow, MenuList, Pagination, RowsPicker, PanelDrawer, StatTile, SummaryStat,
  filterBtn, ghostBtn, miniLabel,
} from '../components/Stock/stockChrome.jsx';

import { PRIMARY, shellAntdTheme, shellVars, STATUS } from '../shellTokens';

const { RangePicker } = DatePicker;

const PAGE_SIZE_OPTIONS = [15, 30, 50];

/**
 * Named ranges for the picker, over the issue date.
 *
 * "This month" leads, as it does on Sales: the floor's consumption is read
 * month by month against what came in over the same weeks.
 */
const PRESETS = [
  { key: 'thisMonth', label: 'This month', range: () => [dayjs().startOf('month'), dayjs().endOf('month')] },
  { key: 'lastMonth', label: 'Last month', range: () => [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
  { key: 'last90', label: 'Last 90 days', range: () => [dayjs().subtract(90, 'day'), dayjs()] },
  { key: 'all', label: 'All time', range: () => null },
];

const fmtKg = (n) => Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (n) => Number(n ?? 0).toLocaleString();
const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

/** Every fabric on one issue, written the way the drawer writes one line. */
const lineLabel = (l) => [l.fabricType, l.fabricPattern, l.fabricColor].filter(Boolean).join(' · ');

const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
};

/**
 * Fluid columns with a floor, as on Sales: the row fits the width it is given
 * rather than forcing the table to scroll sideways. The two date columns floor
 * at what "12 Aug 25" needs; the fabric column is the one that gives.
 */
const GRID = [
  'minmax(72px,0.9fr)',  // issued
  'minmax(72px,0.9fr)',  // submitted
  'minmax(0,2.6fr)',     // fabric
  'minmax(78px,1fr)',    // kg
  'minmax(58px,0.7fr)',  // rolls
  'minmax(0,1.2fr)',     // issued by
].join(' ');

/** Numeric cells: right-aligned, never wrapping, clipped rather than widening. */
const numCell = {
  padding: '11px 8px', textAlign: 'right', font: '600 12.5px monospace',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

/** How many fabrics the rail lists before deferring to the table. */
const FABRIC_LIMIT = 12;

/**
 * What the store has issued to our own production floor.
 *
 * The PRODUCTION half of the fabric outflow ledger — the same documents Fabric
 * Store writes when you issue cloth, listed on their own instead of only
 * surfacing as PRODUCTION_OUT rows in a shelf's movement history.
 *
 * Deliberately has no money on it. A production issue has no buyer and no
 * price: the figures that exist are the date, the cloth, the weight and the
 * rolls, and a column of zero taka would read as a sale that was never paid.
 *
 * The two dates are not the same date, and the difference is the point:
 * **Issued** is the date written on the slip — when the cloth actually left the
 * store — and **Submitted** is when that slip reached the system. A slip
 * submitted days after the cloth walked out is the thing worth seeing.
 */
function FabricProduction() {
  const axiosClient = useAxiosClient();
  const { darkMode } = useStateContext();
  const isMobile = useIsMobile();
  const { fabricTypes } = useFabricTypes();

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const [preset, setPreset] = useState('thisMonth');
  const [rangeMenuOpen, setRangeMenuOpen] = useState(false);
  const [range, setRange] = useState(() => PRESETS[0].range());
  const [fabricMenuOpen, setFabricMenuOpen] = useState(false);
  const [fabricTypeId, setFabricTypeId] = useState(null);

  const [detail, setDetail] = useState(null);

  const fetchIssues = useCallback(async (overrides = {}) => {
    const p = overrides.page ?? page;
    const size = overrides.pageSize ?? pageSize;
    const r = 'range' in overrides ? overrides.range : range;
    const fabric = 'fabricTypeId' in overrides ? overrides.fabricTypeId : fabricTypeId;

    setLoading(true);
    try {
      const res = await axiosClient.get('/fabricStock/outflows', {
        params: {
          purpose: 'PRODUCTION',
          page: p,
          perPage: size,
          from: r?.[0] ? r[0].format('YYYY-MM-DD') : undefined,
          to: r?.[1] ? r[1].format('YYYY-MM-DD') : undefined,
          fabricTypeId: fabric ?? undefined,
        },
      });
      setRows(res.data.data || []);
      setSummary(res.data.summary || null);
      setTotal(res.data.meta?.total || 0);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not load production issues');
    } finally {
      setLoading(false);
    }
  }, [axiosClient, page, pageSize, range, fabricTypeId]);

  useEffect(() => { fetchIssues(); }, [fetchIssues]);

  const applyPreset = (key) => {
    // "Custom" only reveals the picker — it names how the range is being set,
    // not a range of its own, so the one on screen stands until the picker
    // changes it.
    if (key === 'custom') {
      setPreset('custom');
      return;
    }
    const found = PRESETS.find((p) => p.key === key);
    const next = found ? found.range() : null;
    setPreset(key);
    setRange(next);
    setPage(1);
    fetchIssues({ range: next, page: 1 });
  };

  const applyRange = (value) => {
    setPreset('custom');
    setRange(value);
    setPage(1);
    fetchIssues({ range: value, page: 1 });
  };

  const applyFabric = (value) => {
    setFabricTypeId(value);
    setPage(1);
    fetchIssues({ fabricTypeId: value, page: 1 });
  };

  /** Exports the page on screen — the rail already covers the whole range. */
  const handleExport = () => {
    if (!rows.length) {
      toast.info('Nothing to export');
      return;
    }
    const head = ['Issued', 'Submitted', 'Fabric', 'Weight (kg)', 'Rolls', 'Issued by', 'Note'];
    const body = rows.map((r) => [
      r.happenedOn,
      r.createdAt ? dayjs(r.createdAt).format('YYYY-MM-DD') : '',
      (r.lines || []).map((l) => `${lineLabel(l)} ${l.weight}kg`).join(' | '),
      r.totalWeight ?? '',
      r.totalRolls ?? '',
      r.user ?? '',
      r.note ?? '',
    ]);
    const csv = [head, ...body].map((line) => line.map(csvCell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `fabric-production-${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const rootStyle = shellVars(darkMode);

  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  const rangeLabel = range?.[0]
    ? `${range[0].format('D MMM YYYY')} – ${range[1].format('D MMM YYYY')}`
    : 'All time';

  const presetLabel = preset === 'custom'
    ? 'Custom'
    : PRESETS.find((p) => p.key === preset)?.label || 'Range';

  const fabricName = useMemo(
    () => fabricTypes.find((f) => f.id === fabricTypeId)?.name,
    [fabricTypes, fabricTypeId],
  );

  const byFabric = summary?.byFabric || [];
  const hasBreakdown = byFabric.length > 0;
  const shownFabrics = byFabric.slice(0, FABRIC_LIMIT);
  const hiddenFabrics = Math.max(0, byFabric.length - shownFabrics.length);
  // The heaviest fabric's weight, so the bars below are read against the
  // leader rather than against a total nobody can see.
  const topWeight = byFabric[0]?.weight || 0;

  return (
    <div className="et-prod" style={rootStyle}>
      <style>{`
        .et-prod{font-family:Outfit,system-ui,sans-serif;color:var(--text);display:flex;flex-direction:column;gap:14px;height:100%;min-height:0}
        .et-prod__row:hover{background:var(--hover)}
        .et-prod__ghost:hover{background:var(--hover)}
        .et-prod__fabricrow:hover{background:var(--hover)}

        /* The same rail Sales uses: the breakdown is analysis of the rows, so
           it sits beside them rather than taking height off the one thing on
           the screen meant to fill the viewport. */
        .et-prod__body{flex:1;min-height:0;display:grid;grid-template-columns:minmax(0,1fr);gap:14px;overflow:hidden}
        .et-prod__table{display:flex;flex-direction:column;min-height:0;height:100%;background:var(--surface);border:1px solid var(--border);border-radius:14px;overflow:hidden}
        .et-prod__rail{display:flex;flex-direction:column;gap:14px;min-height:0}
        @media (max-width:1179px){
          .et-prod__body--rail{overflow-y:auto}
          .et-prod__body--rail .et-prod__table{height:min(560px,70vh)}
        }
        @media (min-width:1180px){
          .et-prod__body--rail{grid-template-columns:minmax(0,1fr) 320px}
          .et-prod__body--rail .et-prod__rail{overflow-y:auto}
        }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ font: '700 22px Outfit, sans-serif', letterSpacing: '-.01em' }}>Issued to Production</div>
          <div style={{ font: '400 13px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>
            Raw fabric issued from the store to our own floor · {rangeLabel}
          </div>
        </div>
        <button type="button" className="et-prod__ghost" onClick={handleExport} style={ghostBtn}>
          <DownloadOutlined style={{ color: 'var(--muted)', fontSize: 12 }} /> Export
        </button>
      </div>

      <ConfigProvider theme={shellAntdTheme(darkMode)}>
        {/* KPI tiles — across the whole range, not the page on screen. */}
        {summary && (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 14 }}>
            <StatTile
              icon="▦" tint={PRIMARY} label="Fabric issued"
              value={`${fmtKg(summary.weight)} kg`}
              foot={`${fmtInt(summary.rolls)} roll${summary.rolls === 1 ? '' : 's'}`}
            />
            <StatTile
              icon="↗" tint="var(--activeText)" label="Issues"
              value={fmtInt(summary.issues)}
              foot={`${rangeLabel}`}
            />
            <StatTile
              icon="◈" tint="#a78bfa" label="Fabrics"
              value={fmtInt(byFabric.length)}
              foot={byFabric[0] ? `${byFabric[0].fabricType} leads` : '—'}
            />
            <StatTile
              icon="~" tint={STATUS.ok} label="Average issue"
              value={summary.issues > 0 ? `${fmtKg(summary.weight / summary.issues)} kg` : '—'}
              foot="per slip"
            />
          </div>
        )}

        <div className={`et-prod__body${hasBreakdown ? ' et-prod__body--rail' : ''}`}>
        {/* Table */}
        <div className="et-prod__table">
          {/* Toolbar */}
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <div style={{ font: '700 14.5px Outfit, sans-serif' }}>Issues</div>
              <div style={{ font: '600 11.5px monospace', color: 'var(--dim)', padding: '2px 7px', borderRadius: 9, background: 'var(--surface2)' }}>{total}</div>
            </div>
            <div className="et-toolbar">
              <Popover
                trigger="click"
                open={rangeMenuOpen}
                onOpenChange={setRangeMenuOpen}
                content={
                  <MenuList
                    darkMode={darkMode}
                    items={[...PRESETS.map((p) => ({ key: p.key, label: p.label })), { key: 'custom', label: 'Custom range…' }]}
                    active={preset}
                    onSelect={(key) => { setRangeMenuOpen(false); applyPreset(key); }}
                  />
                }
              >
                <button type="button" style={filterBtn}>
                  {presetLabel} <DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} />
                </button>
              </Popover>
              {/* Only once a custom range is actually being set — otherwise it
                  restates what the button beside it already says. */}
              {preset === 'custom' && (
                <RangePicker
                  value={range}
                  onChange={applyRange}
                  format="D MMM YY"
                  style={{ width: isMobile ? '100%' : 230 }}
                />
              )}
              <Popover
                trigger="click"
                open={fabricMenuOpen}
                onOpenChange={setFabricMenuOpen}
                content={
                  <MenuList
                    darkMode={darkMode}
                    searchPlaceholder="Search fabric"
                    items={[
                      { key: 'all', label: 'All fabrics' },
                      ...fabricTypes.map((f) => ({ key: f.id, label: f.name })),
                    ]}
                    active={fabricTypeId ?? 'all'}
                    onSelect={(key) => { setFabricMenuOpen(false); applyFabric(key === 'all' ? null : key); }}
                  />
                }
              >
                <button type="button" style={filterBtn}>
                  {fabricName || 'All fabrics'} <DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} />
                </button>
              </Popover>
            </div>
          </div>

          {/* Filter chips — the same row the other Store screens carry, so an
              active filter is stated in one place across all of them. */}
          {(fabricTypeId !== null || preset !== 'thisMonth') && (
            <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--border)', background: 'var(--bg)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ ...miniLabel, marginRight: 2 }}>Filters</span>
              {preset !== 'thisMonth' && (
                <FilterChip label="Range" value={rangeLabel} onClear={() => applyPreset('thisMonth')} />
              )}
              {fabricTypeId !== null && (
                <FilterChip label="Fabric" value={fabricName || fabricTypeId} onClear={() => applyFabric(null)} />
              )}
            </div>
          )}

          {/* Desktop rows */}
          {!isMobile && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: GRID, background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                  {['Issued', 'Submitted', 'Fabric', 'Weight', 'Rolls', 'Issued by'].map((h, i) => (
                    <div key={h} style={{
                      padding: i === 0 ? '9px 12px' : '9px 8px', display: 'flex', alignItems: 'center',
                      justifyContent: i === 3 || i === 4 ? 'flex-end' : 'flex-start',
                      font: '700 11.5px Outfit, sans-serif', color: 'var(--muted)',
                      letterSpacing: '.05em', textTransform: 'uppercase',
                      // A header that refuses to shrink would reintroduce the
                      // horizontal scroll the fluid grid exists to remove.
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{h}</div>
                  ))}
                </div>

                {loading && <LoadingRow />}
                {!loading && rows.length === 0 && <EmptyState />}

                {!loading && rows.map((r) => (
                  <div
                    key={r.id}
                    className="et-prod__row"
                    onClick={() => setDetail(r)}
                    style={{ display: 'grid', gridTemplateColumns: GRID, borderBottom: '1px solid var(--border)', alignItems: 'center', cursor: 'pointer' }}
                  >
                    <div style={{ padding: '11px 12px', font: '600 12px monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.happenedOn ? dayjs(r.happenedOn).format('D MMM YY') : '—'}
                    </div>
                    <div style={{ padding: '11px 8px', font: '400 12px monospace', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.createdAt ? dayjs(r.createdAt).format('D MMM YY') : '—'}
                    </div>
                    <div style={{ padding: '11px 8px', font: '400 12px Outfit, sans-serif', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {(r.lines || []).map((l) => `${l.fabricType} ${fmtKg(l.weight)}kg`).join(', ') || '—'}
                    </div>
                    <div style={numCell}>{fmtKg(r.totalWeight)}</div>
                    <div style={{ ...numCell, color: 'var(--muted)' }}>{fmtInt(r.totalRolls)}</div>
                    <div style={{ padding: '11px 12px 11px 8px', font: '400 11.5px Outfit, sans-serif', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.user || '—'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mobile cards */}
          {isMobile && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {loading && <LoadingRow />}
              {!loading && rows.length === 0 && <EmptyState />}
              {!loading && rows.map((r) => (
                <div key={r.id} onClick={() => setDetail(r)} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: 'var(--surface2)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ font: '600 13.5px Outfit, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {(r.lines || []).map((l) => l.fabricType).join(', ') || 'Issue'}
                      </div>
                      <div style={{ font: '400 11.5px monospace', color: 'var(--muted)', marginTop: 2 }}>
                        issued {r.happenedOn ? dayjs(r.happenedOn).format('D MMM YY') : '—'}
                        {' · '}
                        submitted {r.createdAt ? dayjs(r.createdAt).format('D MMM YY') : '—'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flex: 'none' }}>
                      <div style={{ font: '700 14px monospace' }}>{fmtKg(r.totalWeight)} kg</div>
                      <div style={{ font: '500 11.5px monospace', color: 'var(--dim)' }}>{fmtInt(r.totalRolls)} rolls</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          <div style={{ padding: '11px 18px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>
              {total} issue{total === 1 ? '' : 's'}
              {summary && <> · <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{fmtKg(summary.weight)} kg</span> to the floor</>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <RowsPicker
                value={pageSize}
                options={PAGE_SIZE_OPTIONS}
                darkMode={darkMode}
                onChange={(v) => { setPageSize(v); setPage(1); fetchIssues({ pageSize: v, page: 1 }); }}
              />
              <Pagination
                page={page}
                totalPages={lastPage}
                onGo={(p) => { setPage(p); fetchIssues({ page: p }); }}
              />
            </div>
          </div>
        </div>

        {/* Which cloth the floor is eating — beside the rows, not above them. */}
        {hasBreakdown && (
          <div className="et-prod__rail">
            <Panel title="By fabric" hint="Heaviest first">
              {shownFabrics.map((f) => {
                const pct = topWeight > 0 ? (f.weight / topWeight) * 100 : 0;
                const isFiltered = f.fabricTypeId === fabricTypeId;
                return (
                  <div
                    key={f.fabricTypeId}
                    role="button" tabIndex={0}
                    title={`Filter the table to ${f.fabricType}`}
                    className="et-prod__fabricrow"
                    onClick={() => applyFabric(isFiltered ? null : f.fabricTypeId)}
                    onKeyDown={(e) => { if (e.key === 'Enter') applyFabric(isFiltered ? null : f.fabricTypeId); }}
                    style={{ padding: 8, borderRadius: 8, borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ font: '600 12.5px Outfit, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.fabricType}
                      </span>
                      <span style={{ font: '600 12.5px monospace', color: 'var(--muted)', flex: 'none' }}>{fmtKg(f.weight)} kg</span>
                    </div>
                    <div style={{ height: 5, borderRadius: 3, background: 'var(--surface2)', marginTop: 5, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: isFiltered ? 'var(--activeText)' : PRIMARY, borderRadius: 3 }} />
                    </div>
                    <div style={{ font: '400 11px Outfit, sans-serif', color: 'var(--dim)', marginTop: 3 }}>
                      {f.issues} issue{f.issues === 1 ? '' : 's'} · {fmtInt(f.rolls)} roll{f.rolls === 1 ? '' : 's'}
                    </div>
                  </div>
                );
              })}
              {hiddenFabrics > 0 && (
                <div style={{ padding: '8px 8px 0', font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)' }}>
                  {hiddenFabrics} more fabric{hiddenFabrics === 1 ? '' : 's'} in the table below.
                </div>
              )}
            </Panel>
          </div>
        )}
        </div>

        {/* One issue */}
        <PanelDrawer
          open={!!detail}
          onClose={() => setDetail(null)}
          darkMode={darkMode}
          width={isMobile ? '100%' : 460}
          title="Issued to production"
          subtitle={detail?.happenedOn ? dayjs(detail.happenedOn).format('D MMMM YYYY') : undefined}
          bodyPadding={18}
        >
          {detail && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, paddingBottom: 14, borderBottom: '1px solid var(--border)' }}>
                <SummaryStat label="Weight" value={`${fmtKg(detail.totalWeight)} kg`} tint="var(--text)" />
                <SummaryStat label="Rolls" value={fmtInt(detail.totalRolls)} tint="var(--muted)" />
                <SummaryStat label="Fabrics" value={fmtInt((detail.lines || []).length)} tint="var(--muted)" />
              </div>

              {/* Both dates spelled out, because the column headers have room
                  for one word each and the two are easy to read as one. */}
              <div style={{ padding: '12px 0', borderBottom: '1px solid var(--border)', font: '400 12.5px/1.7 Outfit, sans-serif', color: 'var(--muted)' }}>
                <div>Issued on {detail.happenedOn ? dayjs(detail.happenedOn).format('D MMMM YYYY') : '—'}</div>
                <div>
                  Submitted {detail.createdAt ? dayjs(detail.createdAt).format('D MMMM YYYY, h:mm A') : '—'}
                  {detail.user && <> by {detail.user}</>}
                </div>
              </div>

              <div style={{ paddingTop: 12 }}>
                <div style={{ ...miniLabel, marginBottom: 8 }}>Fabric issued</div>
                {(detail.lines || []).map((l) => (
                  <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ font: '400 12.5px Outfit, sans-serif' }}>{lineLabel(l)}</span>
                    <span style={{ font: '600 12.5px monospace', color: 'var(--muted)', flex: 'none' }}>{fmtKg(l.weight)} kg · {l.rolls} rolls</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, font: '700 13px monospace' }}>
                  <span>Total</span>
                  <span>{fmtKg(detail.totalWeight)} kg · {fmtInt(detail.totalRolls)} rolls</span>
                </div>
              </div>

              {detail.note && (
                <div style={{ marginTop: 14, padding: 10, borderRadius: 8, background: 'var(--surface2)', font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)' }}>
                  {detail.note}
                </div>
              )}
            </>
          )}
        </PanelDrawer>
      </ConfigProvider>
    </div>
  );
}

function Panel({ title, hint, children }) {
  return (
    // flex: none — inside the scrolling rail these must keep their natural
    // height rather than being compressed to fit it.
    <div style={{ flex: 'none', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ font: '700 13px Outfit, sans-serif' }}>{title}</span>
        {hint && <span style={{ font: '400 11px Outfit, sans-serif', color: 'var(--dim)' }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{ padding: '32px 18px 36px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center' }}>
      <span style={{ width: 38, height: 38, borderRadius: 12, background: 'var(--surface2)', color: 'var(--dim)', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>▦</span>
      <div style={{ font: '600 13.5px Outfit, sans-serif' }}>Nothing issued in this range</div>
      <div style={{ font: '400 12.5px/1.5 Outfit, sans-serif', color: 'var(--muted)', maxWidth: 320 }}>
        Fabric sent from the store to our own production floor shows up here. Issue some from the Fabric Store.
      </div>
    </div>
  );
}

export default FabricProduction;
